<script setup>
import { ref } from 'vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import FileUploadWidget from '@/presentation/components/input/FileUploadWidget.vue'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'

const emit = defineEmits(['close', 'success', 'error'])

const saveTriggerRef = ref(null)
const isLoading = ref(false)

const defaultForm = () => ({
  name: '',
  description: '',
  logo: '',
  address: '',
  city: '',
  lat: 0,
  lng: 0,
  phone: '',
  email: '',
  isOpen: true,
  active: true,
  cuisine: [],
  ownerFirstName: '',
  ownerLastName: '',
  ownerPassword: 'Rapidoo2024',
})

const form = ref(defaultForm())

// Handle FileUploadWidget upload result
const handleImageSaved = (data) => {
  if (data?.files?.length > 0 && data.files[0].url) {
    form.value.logo = data.files[0].url
  }
}

const submit = () => {
  if (!form.value.name?.trim()) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Validation', body: 'Le nom du restaurant est requis', type: 'error' })
    return
  }
  
  if (!form.value.address?.trim()) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Validation', body: 'L\'adresse est requise', type: 'error' })
    return
  }

  if (!form.value.email?.trim()) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Validation', body: 'L\'email est requis pour le compte propriétaire', type: 'error' })
    return
  }

  if (!form.value.ownerFirstName?.trim() || !form.value.ownerLastName?.trim()) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Validation', body: 'Le nom du propriétaire est requis', type: 'error' })
    return
  }

  if (!form.value.ownerPassword?.trim() || form.value.ownerPassword.length < 6) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Validation', body: 'Le mot de passe doit faire au moins 6 caractères', type: 'error' })
    return
  }

  isLoading.value = true
  const payload = { ...form.value }
  // Provide sensible default description if empty as it might be required by DB schema strictly in some older models, though schema allows empty
  if (!payload.description) {
      payload.description = 'Nouveau restaurant';
  }
  
  saveTriggerRef.value?.trigger(payload)
}

const onSuccess = () => {
  isLoading.value = false
  emit('success')
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Succès', body: 'Le restaurant a été créé avec succès.', type: 'success' })
}

const onError = (err) => {
  isLoading.value = false
  emit('error', err)
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Erreur', body: err?.message || 'Erreur lors de la création', type: 'error' })
}
</script>

<template>
  <!-- Overlay -->
  <div class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 transition-opacity" @click.self="$emit('close')">
    <div class="bg-white w-full max-w-xl rounded-2xl shadow-xl overflow-hidden animate-in zoom-in-95 duration-200">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h2 class="text-lg font-bold text-gray-900">Ajouter un restaurant</h2>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 transition-colors p-2 -mr-2 rounded-full hover:bg-gray-100">
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <!-- Form -->
      <div class="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto custom-scrollbar">

        <!-- Image Upload -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Logo du restaurant</label>
          <FileUploadWidget
            :defaultUrls="form.logo ? [form.logo] : []"
            :maxFiles="1"
            :acceptedFiles="['image/jpeg', 'image/png', 'image/webp']"
            @saved="handleImageSaved"
          />
        </div>

        <!-- Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Nom du restaurant <span class="text-rose-500">*</span></label>
          <input v-model="form.name" type="text" placeholder="Ex: Chez Maman" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
        </div>

        <!-- address & City -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Adresse <span class="text-rose-500">*</span></label>
            <input v-model="form.address" type="text" placeholder="Ex: 123 Rue de la Paix" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Ville</label>
            <input v-model="form.city" type="text" placeholder="Ex: Dakar" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
          </div>
        </div>

        <!-- Owner Account Section -->
        <div class="pt-2">
          <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Compte Propriétaire</h3>
          <div class="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Prénom du propriétaire <span class="text-rose-500">*</span></label>
              <input v-model="form.ownerFirstName" type="text" placeholder="Prénom" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Nom du propriétaire <span class="text-rose-500">*</span></label>
              <input v-model="form.ownerLastName" type="text" placeholder="Nom" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Mot de passe par défaut <span class="text-rose-500">*</span></label>
            <input v-model="form.ownerPassword" type="text" placeholder="Mot de passe" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            <p class="text-[10px] text-gray-500 mt-1">L'utilisateur pourra changer ce mot de passe plus tard.</p>
          </div>
        </div>

        <!-- Coordinates -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
            <input v-model.number="form.lat" type="number" step="0.000001" placeholder="0.000" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
            <input v-model.number="form.lng" type="number" step="0.000001" placeholder="0.000" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
          </div>
        </div>
        
        <!-- Contact & Cuisine -->
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
              <input v-model="form.phone" type="tel" placeholder="+221..." class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email <span class="text-rose-500">*</span></label>
              <input v-model="form.email" type="email" placeholder="contact@resto.com" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Types de cuisine (séparés par des virgules)</label>
            <input 
              :value="form.cuisine.join(', ')" 
              @input="e => form.cuisine = e.target.value.split(',').map(s => s.trim()).filter(s => s)"
              type="text" 
              placeholder="Ex: Ivoirienne, Sénégalaise, Fast-food" 
              class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" 
            />
            <div class="flex flex-wrap gap-1 mt-2">
              <span v-for="c in form.cuisine" :key="c" class="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px] font-bold">
                {{ c }}
              </span>
            </div>
          </div>
        </div>

        <!-- Settings -->
        <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
          <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Paramètres Initiaux</h3>

          <label class="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" v-model="form.isOpen" class="w-4 h-4 rounded accent-primary-600" />
            <div>
              <span class="text-sm font-medium text-gray-800">Disponible immédiament (Ouvert)</span>
              <p class="text-xs text-gray-500">Le restaurant sera marqué comme ouvert (isOpen)</p>
            </div>
          </label>

          <label class="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" v-model="form.active" class="w-4 h-4 rounded accent-primary-600" />
            <div>
              <span class="text-sm font-medium text-gray-800">Compte Actif</span>
              <p class="text-xs text-gray-500">Le restaurant sera visible et actif sur la plateforme</p>
            </div>
          </label>
        </div>

      </div>

      <!-- Footer buttons -->
      <div class="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50">
        <button @click="$emit('close')" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          Annuler
        </button>
        <button @click="submit" :disabled="isLoading" class="px-6 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 shadow-sm disabled:opacity-60 transition-all flex items-center gap-2">
          <span v-if="isLoading" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
          Créer le restaurant
        </button>
      </div>
    </div>
  </div>

  <!-- Triggers -->
  <ActionTrigger
    dataKey="createRestaurant"
    ref="saveTriggerRef"
    @success="onSuccess"
    @failed="onError"
  />
</template>
